import cloneDeep from 'lodash/fp/cloneDeep'
import { events } from './adviserSearchResultsListContainerActions'
import { result } from 'lodash'
import { events as adviserProfileEvents } from '../../core/actions/adviserProfiles'

const minimumTotalReviews = 5

const initialState = {
  loading: false,
  loadingNextPage: false,
  profileIds: [],
  profilesById: {},
  searchResultsById: {},
  profiles: [],
  lastSort: 'relevance',
  currentPage: 1,
  pageSize: 10,
  hasMorePages: false,
  searchBar: { ifaName: '' }
}

export default function (state = initialState, action) {
  var newState = cloneDeep(state)

  switch (action.type) {
    case events.set.searchResults.currentPage:
      newState.currentPage = action.page
      return rebuildProfiles(newState)
    case events.set.resetFilter:
      newState.currentPage = 1
      newState.lastSort = events.sort.byDistanceAscending
      return rebuildProfiles(newState)
    case events.filter.profiles:
      return rebuildProfiles(newState)
    case events.sort.byRatingDescending:
      newState.currentPage = 1
      newState.lastSort = action.type
      sortProfileIds(newState)
      return rebuildProfiles(newState)
    case events.sort.byDistanceAscending:
      newState.currentPage = 1
      newState.lastSort = action.type
      sortProfileIds(newState)
      return rebuildProfiles(newState)
    case events.fetching.searchResults:
      newState.loading = true
      newState.profileIds = []
      newState.profilesById = {}
      newState.searchResultsById = {}
      newState.profiles = []
      newState.hasMorePages = false
      return newState
    case events.fetched.searchResults: {
      newState.loading = false
      const results = Array.isArray(action.payload) ? action.payload : []
      newState.searchResultsById = {}
      results.forEach(searchRow => {
        if (searchRow && searchRow.adviserId != null) {
          newState.searchResultsById[searchRow.adviserId] = searchRow
        }
      })
      newState.profileIds = results.map(searchRow => searchRow.adviserId)
      // Sort on load so default "Distance" order matches the dropdown.
      sortProfileIds(newState)
      newState.profiles = []
      newState.hasMorePages =
        newState.currentPage * newState.pageSize < results.length
      return newState
    }
    case events.fetching.nextPage:
      newState.loading = false
      newState.loadingNextPage = true
      newState.currentPage = newState.currentPage + 1
      newState.hasMorePages =
        newState.currentPage * newState.pageSize < newState.profileIds.length
      return rebuildProfiles(newState)
    case events.set.searchBar.ifaName:
      newState.searchBar.ifaName = action.ifaName
      return newState
    case events.clear.searchBar.ifaName:
      newState.searchBar.ifaName = ''
      return newState
    case events.clear.results:
      return cloneDeep(initialState)
    case adviserProfileEvents.fetched.publishedProfile: {
      const updatedProfile = action.payload
      newState.profilesById[updatedProfile.adviserId] = updatedProfile
      return rebuildProfiles(newState)
    }
    default:
      return state
  }
}

function rebuildProfiles (newState) {
  const { pageSize, currentPage, profileIds, profilesById = {}, searchResultsById = {} } = newState
  const start = pageSize * (currentPage - 1)
  const end = Math.min(start + pageSize, profileIds.length)
  const currentPageIds = profileIds.slice(start, end)
  newState.profiles = currentPageIds
    .map(id => {
      const profile = profilesById[id]
      if (!profile) return null
      // Published-profile fetch lacks the search API's distance, so merge it back in.
      const searchRow = searchResultsById[id]
      if (searchRow && searchRow.distanceToAdviser !== undefined) {
        return { ...profile, distanceToAdviser: searchRow.distanceToAdviser }
      }
      return profile
    })
    .filter(Boolean)

  return newState
}

// Sort the FULL id list (not just the visible page) via search rows carrying distance + rating.
function sortProfileIds (newState) {
  const records = newState.profileIds
    .map(id => newState.searchResultsById[id])
    .filter(Boolean)

  // Only reorder when we have a search row for every id, so we never drop ids.
  if (records.length !== newState.profileIds.length) {
    return
  }

  switch (newState.lastSort) {
    case events.sort.byRatingDescending:
      records.sort(ratingComparator)
      newState.profileIds = records.map(record => record.adviserId)
      return
    case events.sort.byDistanceAscending:
      records.sort(distanceComparator)
      newState.profileIds = records.map(record => record.adviserId)
      return
    default:
  }
}

function distanceComparator (a, b) {
  var aDistance = a.distanceToAdviser
  var bDistance = b.distanceToAdviser

  if (aDistance === undefined || aDistance === 'Unknown') {
      aDistance = Number.MAX_SAFE_INTEGER
    }
  if (bDistance === undefined || bDistance === 'Unknown') {
      bDistance = Number.MAX_SAFE_INTEGER
    }
  if (typeof aDistance === 'string') {
    aDistance = parseFloat(aDistance.replace(' km', '').replace(',', ''))
  }
  if (typeof bDistance === 'string') {
    bDistance = parseFloat(bDistance.replace(' km', '').replace(',', ''))
  }

  if (aDistance > bDistance) return 1
  if (bDistance > aDistance) return -1

  const aVals = coerceRatingValues(a)
  const bVals = coerceRatingValues(b)
  if (aVals.aggregateRating !== bVals.aggregateRating) {
    return (aVals.aggregateRating - bVals.aggregateRating) * -1
  }
  return (aVals.totalReviews - bVals.totalReviews) * -1
}

function ratingComparator (a, b) {
  const aVals = coerceRatingValues(a)
  const bVals = coerceRatingValues(b)

  if (aVals.aggregateRating !== bVals.aggregateRating) {
    return (aVals.aggregateRating - bVals.aggregateRating) * -1
  }
  if (aVals.totalReviews === bVals.totalReviews) {
    return -1 * distanceComparator(a, b)
  }
  return (aVals.totalReviews - bVals.totalReviews) * -1
}

function coerceRatingValues (record) {
  let aggregateRating
  let totalReviews
  // Support both shapes: published rating { average, totalReviews } and flat search-row fields.
  if (record && typeof record.rating === 'object' && record.rating !== null) {
    aggregateRating = record.rating.average ?? 0
    totalReviews = record.rating.totalReviews ?? 0
  } else {
    aggregateRating = (record && record.aggregateRating) ?? 0
    totalReviews = (record && record.totalReviews) ?? 0
  }
  return {
    aggregateRating: totalReviews > minimumTotalReviews ? aggregateRating : 0,
    totalReviews
  }
}
